import {ClientMessage, deserializeMessage, HostMessage, serializeMessage, Token} from '@hantera/portal-contracts';
import {BackendError, BackendFetch, BackendFetchResponse, Host, Processes} from '@hantera/portal-app';
import type {EventsService as IEventsService} from '@hantera/portal-app/src/composition/use-events';
import type {LiveQueryService as ILiveQueryService} from '@hantera/portal-app/src/composition/use-live-queries';
import {PortalWebSocketClient, WebSocketMessage} from './portal-websocket-client';
import {WebSocketClient} from './websocket-client';
import {EventsService} from './events-service';
import {LiveQueryService} from './live-query-service';
import log from './log';
import {parseResponseJson} from "@/backend";

let hostChannel = new MessageChannel();
let hostPort = hostChannel.port1;

/**
 * Initializes the client and handshakes with the host
 */
export default function (processes: Processes) {
  return new Promise<{
    host: Host
    /**
     * Performs a fetch against Portal server
     * @param path 
     * @param options 
     * @returns 
     */
    portalFetch: BackendFetch

    mode: 'workspace' | 'stack'
    defaultViewUri?: string
    stateKey?: string
    
    serverUrl: string
    instanceName: string
    legacyMode: boolean
    
    webSocketSubscribe(handler: (message: WebSocketMessage) => void): void;
    webSocketUnsubscribe(handler: (message: WebSocketMessage) => void): void;
    eventsService: IEventsService;
    liveQueryService: ILiveQueryService;
  }>((res) => {
    let token: Token | undefined = undefined;

    let portalWebSocket: PortalWebSocketClient;
    let coreWebSocket: WebSocketClient;
    let eventsService: EventsService;
    let liveQueryService: LiveQueryService;
    let clientId: string | undefined;

    hostPort!.onmessage = ((event) => {
      const payload = deserializeMessage(event.data) as ClientMessage;
      if (payload.type === 'handshakeResponse') {

        const serverHost = isDevelopment() ? getDevelopmentServerUrl() : getProductionServerUrl();
        const serverUrl = `https://${serverHost}`;
        token = payload.token;

        if (payload.theme) {
          document.documentElement.dataset.theme = payload.theme;
        }

        function isDevelopment() {
          return document.location.host === 'localhost:3001';
        }
        function getDevelopmentServerUrl() {
          // During development, the portal server runs on a different port
          return 'localhost:3100';
        }
        function getProductionServerUrl() {
          return `${document.location.host}`;
        }
        
        const host = {
          send: post,
        };

        portalWebSocket = new PortalWebSocketClient(
          serverHost,
          () => {
            portalWebSocket.send(JSON.stringify({
              type: 'token',
              token: token?.accessToken
            }));
          },
          processes
        );

        coreWebSocket = new WebSocketClient(serverHost, () => token?.accessToken);
        eventsService = new EventsService(coreWebSocket);
        liveQueryService = new LiveQueryService(coreWebSocket);
        coreWebSocket.connect();
        
        // Compatibility code as we're moving toward portal mode instead of routes
        const mode = payload.mode ?? ((payload.path?.indexOf('workspace') ?? -1) >= 0 ? 'workspace' : 'stack')

        res({
          webSocketSubscribe: (handler: (message: WebSocketMessage) => void) => portalWebSocket.subscribe(handler),
          webSocketUnsubscribe: (handler: (message: WebSocketMessage) => void) => portalWebSocket.unsubscribe(handler),
          mode,
          defaultViewUri: payload.path,
          serverUrl,
          instanceName: token.tenantName,
          stateKey: payload.stateKey,
          portalFetch: (path: RequestInfo, options?: RequestInit) => {
            path = serverUrl + path;
            
            let attempts = 0;
            
            return tryFetch();
            
            function tryFetch(): any {
              attempts += 1;
              
              return fetch(path, {
                ...options,
                headers: {
                  ...(!(options?.body instanceof FormData) && { 'Content-Type': 'application/json' }),
                  'Authorization': `Bearer ${token?.accessToken}`,
                  'Client-Id': clientId ?? '',
                  ...options?.headers,
                },
              }).then(async r => {
                if (r.status === 401) {

                  if (attempts > 5) {
                    post({
                      type: 'expiredSession'
                    });
                    return;
                  }
                  
                  
                  // There's a race condition where another tab might have refreshed the token, and it has not
                  // have time to propagate to this instance yet. Therefor, we ask the host to provide the
                  // current token and if it's different, retry the request.
                  
                  log.debug('Fetch failed, possibly stable token, verifying...');

                  const newToken = await postGet<Token>({
                    type: 'getToken'
                  });

                  log.debug('Retrying fetch..');

                  token = newToken;
                  if (portalWebSocket.isOpen()) {
                    portalWebSocket.send(JSON.stringify({
                      type: 'token',
                      token: token?.accessToken
                    }));
                  }

                  return tryFetch();
                }
                
                r.json = async () => {
                  return parseResponseJson(await r.text())
                }
                
                (r as BackendFetchResponse).graph = async () => {
                  return parseResponseJson(await r.text(), true)
                }

                (r as BackendFetchResponse).errors = async () => {
                  
                  if (r.headers.get('Content-Type')?.startsWith('application/json')) {
                    const value = await r.json();
                    if (value.hasOwnProperty('errors')) {
                      const errors = value.errors
                      if (!Array.isArray(errors) || errors.length === 0) {
                        return [{
                          code: 'UNKNOWN',
                          message: 'Server responded: ' + JSON.stringify(value)
                        }] as [BackendError, ...BackendError[]]
                      }
                      return errors as [BackendError, ...BackendError[]]
                    } else {
                      return [{
                        code: 'UNKNOWN',
                        message: 'Server responded: ' + JSON.stringify(value)
                      }] as [BackendError, ...BackendError[]]
                    }
                  }

                  return [{
                    code: 'UNKNOWN',
                    message: 'Server responded: ' + await r.text()
                  }] as [BackendError, ...BackendError[]]
                }
                
                return r as BackendFetchResponse;
              });
            }
            
            
          },
          host,
          legacyMode: token?.legacyMode || false,
          eventsService,
          liveQueryService,
        });
      } else if (payload.type === 'tokenRefreshed') {
        const previousAccessToken = token?.accessToken;
        token = payload.token;
        if (previousAccessToken !== payload.token.accessToken) {
          log.debug('Received new token from host');
          portalWebSocket.send(JSON.stringify({
            type: 'token',
            token: token?.accessToken
          }));
          coreWebSocket.sendAuth();
        }
      } else if (payload.type === 'setTheme') {
        (window as any).setTheme(payload.theme);
      }
    });
  
    parent.postMessage({
      type: 'handshake',
    }, '*', [hostChannel.port2]);
  });
}

class PortalProcessError extends Error { }

function post(message: HostMessage) {
  if (!hostPort) {
    throw new Error('Host not connected');
  }

  hostPort.postMessage(serializeMessage(message));
}

function postGet<T>(message: HostMessage) {
  if (!hostPort) {
    throw new Error('Host not connected');
  }

  return new Promise<T>((resolve, reject) => {
    let replyChannel = new MessageChannel();
    let replyPort = replyChannel.port1;
    
    hostPort.postMessage(serializeMessage(message), [replyChannel.port2]);
    
    replyPort.onmessage = (event) => {
      resolve(event.data);
    };
    replyPort.onmessageerror = (event) => {
      reject(event.data);
    };
  });
}
